/**
 * HOW BIG A FILE IS, in words. One implementation, because there were three.
 *
 * FileBrowser, chat/Attachment and notes/NoteAttachments each had their own, and they
 * disagreed in ways a person could see:
 *
 *   · A ZERO-BYTE FILE. Attachment's guard was `if (!size) return ''`, which catches 0
 *     as well as undefined — so an empty file showed no size at all, reading as "the
 *     size is still loading" rather than "this file is empty". An empty file is a real
 *     thing, usually an interesting one. FileBrowser got this right with an explicit
 *     null/undefined check, which is the one kept here.
 *   · THE CEILING. MB in notes, GB in chat, TB in the file browser. The note-file limit
 *     is 100 MB so notes never noticed, but /fs/upload accepts up to 5 GB — a browser
 *     capped at MB would have printed "5120.0 MB" for it. Anything that stops early
 *     eventually prints a number nobody can read.
 *   · PRECISION. Notes rounded KB to a whole number (1536 B → "2 KB"); the other two
 *     gave a decimal below 10 (→ "1.5 KB"). The decimal is kept: at the KB end the
 *     rounding is a third of the actual value.
 *
 * Bytes stay a whole number — "1.5 B" is not a size, and the fractional part of a byte
 * count is noise from a division that should not have happened.
 */

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const

export function formatBytes(size: number | null | undefined): string {
  // Explicitly null/undefined, NOT falsy: 0 is a size, and `!size` swallowing it is the
  // bug this replaced.
  if (size === null || size === undefined || !Number.isFinite(size)) return ''

  let n = size
  let i = 0
  while (n >= 1024 && i < UNITS.length - 1) {
    n /= 1024
    i++
  }
  // One decimal below 10 — "1.5 KB" says something "2 KB" does not; above 10 the decimal
  // is noise. Bytes are never fractional.
  return `${i === 0 ? n : n.toFixed(n < 10 ? 1 : 0)} ${UNITS[i]}`
}
